import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { MapView } from "@/components/Map";
import { Button } from "@/components/ui/button";
import { Satellite } from "lucide-react";
import type { MapsFrontendFailure } from "@/lib/mapsFrontendLoader";
import { commercialLocationZoom } from "@/lib/commercialLocation";
import { PnoaPinFallback } from "./PnoaPinFallback";

export type Pin = { latitude: number; longitude: number };
export type PinSource = "google_maps" | "manual";

export function CommercialPinMap({
  value,
  onChange,
  disabled = false,
  previewCenter = null,
  previewPrecision,
  locationPrecision,
  onFallbackSearch,
  mapVisible = true,
  pinInteractionEnabled = true,
  onMapReadyShared,
  googleMapChildren,
  onRoofModeRequest,
}: {
  value: Pin | null;
  onChange: (value: Pin, source?: PinSource) => void;
  disabled?: boolean;
  previewCenter?: Pin | null;
  previewPrecision?: string;
  locationPrecision?: string;
  onFallbackSearch?: () => void;
  mapVisible?: boolean;
  pinInteractionEnabled?: boolean;
  onMapReadyShared?: (map: google.maps.Map) => void;
  googleMapChildren?: (map: google.maps.Map, moveMapViewport: (target: HTMLElement | null) => void) => ReactNode;
  onRoofModeRequest?: () => void;
}) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapHomeSlotRef = useRef<HTMLElement | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const renderingListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const latestProps = useRef({ onChange, disabled, pinInteractionEnabled });
  latestProps.current = { onChange, disabled, pinInteractionEnabled };
  const [available, setAvailable] = useState(true);
  const [failure, setFailure] = useState<MapsFrontendFailure | null>(null);
  const [rasterRendering, setRasterRendering] = useState(false);
  const [mapType, setMapType] = useState<"roadmap" | "satellite">("satellite");
  const [attempt, setAttempt] = useState(0);
  const lastAutoCenter = useRef("");

  const apply = useCallback((map: google.maps.Map, pin: Pin | null) => {
    if (!pin) {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      return;
    }
    const point = { lat: pin.latitude, lng: pin.longitude };
    const key = `${pin.latitude.toFixed(6)},${pin.longitude.toFixed(6)}`;
    if (lastAutoCenter.current !== key) {
      map.setCenter(point);
      map.setZoom(commercialLocationZoom(locationPrecision));
      lastAutoCenter.current = key;
    }
    if (!markerRef.current) {
      markerRef.current = new google.maps.marker.AdvancedMarkerElement({ map, position: point, gmpDraggable: !disabled && latestProps.current.pinInteractionEnabled, title: "Ubicación propuesta" });
      markerRef.current.addListener("dragend", () => {
        if (latestProps.current.disabled || !latestProps.current.pinInteractionEnabled) return;
        const position = markerRef.current?.position;
        if (position && typeof position !== "string" && "lat" in position && "lng" in position) {
          const latitude = typeof position.lat === "function" ? position.lat() : position.lat;
          const longitude = typeof position.lng === "function" ? position.lng() : position.lng;
          lastAutoCenter.current = `${Number(latitude).toFixed(6)},${Number(longitude).toFixed(6)}`;
          latestProps.current.onChange({ latitude: Number(latitude), longitude: Number(longitude) }, "google_maps");
        }
      });
    } else {
      markerRef.current.position = point;
      markerRef.current.gmpDraggable = !disabled && latestProps.current.pinInteractionEnabled;
    }
  }, [disabled, locationPrecision]);

  const moveMapViewport = useCallback((target: HTMLElement | null) => {
    const map = mapRef.current;
    const container = map?.getDiv();
    const home = mapHomeSlotRef.current;
    const destination = target ?? home;
    if (container && destination && container.parentElement !== destination) destination.appendChild(container);
    if (home) {
      home.style.height = target ? "0px" : "";
      home.style.minHeight = target ? "0px" : "";
    }
  }, []);

  const onMapReady = useCallback((map: google.maps.Map) => {
    const isSameMap = mapRef.current === map;
    mapRef.current = map;
    if (!isSameMap) mapHomeSlotRef.current = map.getDiv().parentElement;
    if (!isSameMap) onMapReadyShared?.(map);
    map.setMapTypeId(mapType);
    if (!isSameMap) mapClickListenerRef.current = map.addListener("click", (event: google.maps.MapMouseEvent) => {
      if (latestProps.current.disabled || !latestProps.current.pinInteractionEnabled || !event.latLng) return;
      lastAutoCenter.current = `${event.latLng.lat().toFixed(6)},${event.latLng.lng().toFixed(6)}`;
      latestProps.current.onChange({ latitude: event.latLng.lat(), longitude: event.latLng.lng() }, "google_maps");
    });
    const updateRenderingMode = () => setRasterRendering(map.getRenderingType?.() === "RASTER");
    updateRenderingMode();
    if (!isSameMap) renderingListenerRef.current = map.addListener("renderingtype_changed", updateRenderingMode);
    apply(map, value);
    if (!value && previewCenter) {
      const key = `${previewCenter.latitude.toFixed(6)},${previewCenter.longitude.toFixed(6)}`;
      if (lastAutoCenter.current !== key) { map.setCenter({ lat: previewCenter.latitude, lng: previewCenter.longitude }); map.setZoom(commercialLocationZoom(previewPrecision ?? locationPrecision)); lastAutoCenter.current = key; }
    }
  }, [apply, mapType, previewCenter, previewPrecision, locationPrecision, value, onMapReadyShared]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setMapTypeId(mapType);
      apply(mapRef.current, value);
      if (!value && previewCenter) {
        const key = `${previewCenter.latitude.toFixed(6)},${previewCenter.longitude.toFixed(6)}`;
        if (lastAutoCenter.current !== key) { mapRef.current.setCenter({ lat: previewCenter.latitude, lng: previewCenter.longitude }); mapRef.current.setZoom(commercialLocationZoom(previewPrecision ?? locationPrecision)); lastAutoCenter.current = key; }
      }
    }
  }, [apply, mapType, previewCenter, previewPrecision, locationPrecision, value, pinInteractionEnabled]);

  useEffect(() => () => {
    mapClickListenerRef.current?.remove();
    renderingListenerRef.current?.remove();
    if (markerRef.current) markerRef.current.map = null;
  }, []);

  return <div className="space-y-2">
    {available ? <>
      {rasterRendering && mapVisible && <p role="status" className="text-xs text-muted-foreground">Google Maps se está mostrando en modo raster; el mapa sigue disponible.</p>}
      {mapVisible && <div className="flex flex-wrap justify-end gap-2"><Button type="button" size="touch" variant="outline" onClick={() => setMapType(old => old === "roadmap" ? "satellite" : "roadmap")}><Satellite className="mr-2 h-4 w-4" />{mapType === "roadmap" ? "Satélite" : "Mapa"}</Button>{pinInteractionEnabled && <Button type="button" size="touch" variant="outline" disabled={!value || disabled} onClick={() => onRoofModeRequest?.()}>Dibujar cubiertas</Button>}</div>}
      <MapView
        key={attempt}
        className={`h-[clamp(380px,58vh,680px)] min-h-[380px] w-full rounded-2xl`}
        initialCenter={value ? { lat: value.latitude, lng: value.longitude } : previewCenter ? { lat: previewCenter.latitude, lng: previewCenter.longitude } : { lat: 40.4168, lng: -3.7038 }}
        initialZoom={value ? commercialLocationZoom(locationPrecision) : previewCenter ? commercialLocationZoom(previewPrecision ?? locationPrecision) : 12}
        onMapReady={onMapReady}
        onMapError={kind => {
          mapClickListenerRef.current?.remove();
          renderingListenerRef.current?.remove();
          if (markerRef.current) markerRef.current.map = null;
          mapClickListenerRef.current = null;
          renderingListenerRef.current = null;
          markerRef.current = null;
          mapRef.current = null;
          setFailure(kind);
          setAvailable(false);
        }}
      >{(map: google.maps.Map) => googleMapChildren?.(map, moveMapViewport)}</MapView>
    </> : <>
      <Button type="button" size="touch" variant="outline" onClick={() => {
        mapClickListenerRef.current?.remove();
        renderingListenerRef.current?.remove();
        if (markerRef.current) markerRef.current.map = null;
        mapClickListenerRef.current = null;
        renderingListenerRef.current = null;
        markerRef.current = null;
        mapRef.current = null;
        setRasterRendering(false);
        setFailure(null);
        setAvailable(true);
        setAttempt(value => value + 1);
      }}>Reintentar Google Maps</Button>
      {value && !disabled && <Button type="button" size="touch" variant="outline" onClick={() => onRoofModeRequest?.()}>Dibujar cubiertas con alternativa</Button>}
      <PnoaPinFallback
        value={value}
        center={previewCenter}
        onChange={point => onChange(point, "manual")}
        disabled={disabled}
        failure={failure}
        onSearchAddress={onFallbackSearch}
      />
    </>}
    {available && mapVisible && pinInteractionEnabled && value && <p className="text-xs text-muted-foreground">Punto seleccionado: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)} · puedes mover el pin o tocar el mapa.</p>}
  </div>;
}
